import React, { useEffect, useMemo, useRef, useState } from "react";
import { ComposableMap, Geographies, Geography, ZoomableGroup } from "react-simple-maps";
import { geoMercator } from "d3-geo";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAdminFetch } from "@/pages/admin/_adminCache";
import { Users, Car, RotateCw, Wifi, WifiOff, X, Plus, Minus, RotateCcw } from "lucide-react";

const MAP_WIDTH = 800;
const MAP_HEIGHT = 560;
const projection = geoMercator()
  .center([2.5, 46.5])
  .scale(2300)
  .translate([MAP_WIDTH / 2, MAP_HEIGHT / 2]);

/**
 * Admin — Carte France des utilisateurs géolocalisés.
 *
 * Même technologie que la carte publique (react-simple-maps + d3-geo),
 * zéro dépendance réseau pour les tuiles → impossible d'afficher une
 * page blanche. Différences avec la version publique :
 *   - Expose les noms et IDs réels (admin only)
 *   - Permet de filtrer par catégorie (online / offline / passagers)
 *   - Clic sur un point → popup détaillé
 */
export default function UsersMapTab() {
  const [filter, setFilter] = useState({ driversOnline: true, driversOffline: true, clients: true });
  const [selected, setSelected] = useState(null); // point cliqué
  const [tick, setTick] = useState(0);
  const [view, setView] = useState({ coordinates: [2.5, 46.5], zoom: 1 });

  const { data, loading, refreshing, reload } = useAdminFetch(
    "admin:price:users-map",
    async () => {
      const r = await api.get("/admin/price/users-map");
      return r.data || { drivers: [], clients: [], counts: {} };
    },
    { ttlMs: 60_000, pollMs: 30_000, initialData: { drivers: [], clients: [], counts: {} } },
  );

  // Animation halo pour les drivers online.
  useEffect(() => {
    let f = 0;
    const loop = () => { setTick((v) => (v + 1) % 1_000_000); f = requestAnimationFrame(loop); };
    f = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(f);
  }, []);

  const projectedPoints = useMemo(() => {
    const out = [];
    (data?.drivers || []).forEach((d) => {
      if (d.online && !filter.driversOnline) return;
      if (!d.online && !filter.driversOffline) return;
      const xy = projection([d.lng, d.lat]);
      if (!xy) return;
      out.push({ ...d, x: xy[0], y: xy[1] });
    });
    if (filter.clients) {
      (data?.clients || []).forEach((c) => {
        const xy = projection([c.lng, c.lat]);
        if (!xy) return;
        out.push({ ...c, x: xy[0], y: xy[1] });
      });
    }
    return out;
  }, [data, filter]);

  const now = performance.now();
  const halo = (() => {
    const t = (now % 1800) / 1800;
    return { r: 8 + t * 16, opacity: (1 - t) * 0.45 };
  })();
  const counts = data?.counts || {};

  return (
    <div className="space-y-3" data-testid="admin-users-map-tab">
      <div className="flex items-center justify-between">
        <div>
          <div className="font-[Outfit] text-[18px] font-black">Carte des utilisateurs</div>
          <div className="text-[12px] text-foreground/60 font-medium">
            {counts.drivers_online || 0} covoitureur{(counts.drivers_online || 0) > 1 ? "s" : ""} en ligne ·
            {" "}{counts.drivers_total || 0} total ·
            {" "}{counts.clients_total || 0} passagers
          </div>
        </div>
        <button
          type="button"
          onClick={() => reload(true)}
          className="street-press h-9 w-9 rounded-xl bg-white grid place-items-center street-border-thin street-shadow-sm"
          data-testid="admin-users-map-refresh"
          aria-label="Rafraîchir"
        >
          <RotateCw className={"h-4 w-4 " + ((loading || refreshing) ? "animate-spin" : "")} strokeWidth={2.5} />
        </button>
      </div>

      <div className="flex flex-wrap gap-2">
        <FilterChip
          active={filter.driversOnline}
          onToggle={() => setFilter((f) => ({ ...f, driversOnline: !f.driversOnline }))}
          color="#FF3B3B"
          testid="map-filter-drivers-online"
        >
          <Wifi className="h-3 w-3" strokeWidth={2.8} />
          Covoitureurs en ligne ({counts.drivers_online || 0})
        </FilterChip>
        <FilterChip
          active={filter.driversOffline}
          onToggle={() => setFilter((f) => ({ ...f, driversOffline: !f.driversOffline }))}
          color="#FFA3A3"
          testid="map-filter-drivers-offline"
        >
          <WifiOff className="h-3 w-3" strokeWidth={2.8} />
          Covoitureurs hors-ligne
        </FilterChip>
        <FilterChip
          active={filter.clients}
          onToggle={() => setFilter((f) => ({ ...f, clients: !f.clients }))}
          color="#3B82F6"
          testid="map-filter-clients"
        >
          <Users className="h-3 w-3" strokeWidth={2.8} />
          Passagers ({counts.clients_total || 0})
        </FilterChip>
      </div>

      <div
        className="relative rounded-2xl overflow-hidden street-border-thin street-shadow-sm"
        style={{ background: "linear-gradient(180deg, #FDF4E7 0%, #F5E6D3 100%)" }}
      >
        <ComposableMap
          projection={projection}
          width={MAP_WIDTH}
          height={MAP_HEIGHT}
          viewBox={`0 0 ${MAP_WIDTH} ${MAP_HEIGHT}`}
          style={{ width: "100%", height: "auto", display: "block" }}
        >
          <ZoomableGroup
            center={view.coordinates}
            zoom={view.zoom}
            minZoom={1}
            maxZoom={10}
            onMoveEnd={({ coordinates, zoom }) => setView({ coordinates, zoom })}
          >
            <Geographies geography="/france-metropole.geojson">
              {({ geographies }) =>
                geographies.map((geo) => (
                  <Geography
                    key={geo.rsmKey || "fr"}
                    geography={geo}
                    fill="#F8D5A8"
                    stroke="#1E0910"
                    strokeWidth={0.6}
                    style={{
                      default: { outline: "none" },
                      hover: { outline: "none", fill: "#F8D5A8" },
                      pressed: { outline: "none" },
                    }}
                  />
                ))
              }
            </Geographies>

            {projectedPoints.map((p) => {
              const online = p.role === "driver" && p.online;
              const isDriver = p.role === "driver";
              const dot = online ? "#FF3B3B" : (isDriver ? "#FFA3A3" : "#3B82F6");
              const stroke = isDriver ? "#B00000" : "#1E3A8A";
              const z = view.zoom || 1;
              return (
                <g
                  key={`${p.role}-${p.user_id}`}
                  transform={`translate(${p.x} ${p.y})`}
                  style={{ cursor: "pointer" }}
                  onClick={() => setSelected(p)}
                  data-testid={`map-marker-${p.role}-${p.user_id}`}
                >
                  {online && (
                    <circle r={halo.r / z} fill="none" stroke="#FF3B3B" strokeWidth={1.5 / z} opacity={halo.opacity} />
                  )}
                  <circle r={(online ? 4.5 : 3.5) / z} fill={dot} stroke={stroke} strokeWidth={0.7 / z} />
                </g>
              );
            })}
          </ZoomableGroup>
        </ComposableMap>

        {/* Boutons zoom */}
        <div className="absolute top-3 right-3 flex flex-col gap-1" style={{ zIndex: 10 }}>
          <button
            type="button"
            onClick={() => setView((v) => ({ ...v, zoom: Math.min(v.zoom * 1.6, 10) }))}
            aria-label="Zoom avant"
            className="h-9 w-9 rounded-xl bg-white street-border-thin street-shadow-sm grid place-items-center active:translate-y-[1px]"
          >
            <Plus className="h-4 w-4" strokeWidth={3} />
          </button>
          <button
            type="button"
            onClick={() => setView((v) => ({ ...v, zoom: Math.max(v.zoom / 1.6, 1) }))}
            aria-label="Zoom arrière"
            className="h-9 w-9 rounded-xl bg-white street-border-thin street-shadow-sm grid place-items-center active:translate-y-[1px]"
          >
            <Minus className="h-4 w-4" strokeWidth={3} />
          </button>
          <button
            type="button"
            onClick={() => setView({ coordinates: [2.5, 46.5], zoom: 1 })}
            aria-label="Recentrer"
            className="h-9 w-9 rounded-xl bg-white street-border-thin street-shadow-sm grid place-items-center active:translate-y-[1px]"
          >
            <RotateCcw className="h-4 w-4" strokeWidth={3} />
          </button>
        </div>

        {loading && (
          <div className="absolute inset-0 flex items-center justify-center bg-white/40 pointer-events-none">
            <div className="rounded-xl bg-white px-3 py-2 street-border-thin text-[12px] font-black uppercase">
              Chargement…
            </div>
          </div>
        )}

        {/* Popup détail */}
        {selected && (
          <div
            className="absolute left-3 right-3 bottom-3 sm:left-auto sm:right-3 sm:w-72 rounded-2xl bg-white street-border px-3 py-3 flex items-start gap-3"
            style={{ boxShadow: "3px 3px 0 0 #1E0910" }}
            data-testid="map-popup"
          >
            <div className="flex-1 min-w-0">
              <div className="font-[Outfit] text-[14px] font-black truncate">
                {selected.name}
              </div>
              <div className="text-[10px] font-black uppercase tracking-widest text-foreground/60 mt-0.5">
                {selected.role === "driver"
                  ? (selected.online ? "🟢 Covoitureur en ligne" : "⚫ Covoitureur hors-ligne")
                  : "🔵 Passager"}
              </div>
              {selected.last_seen && (
                <div className="text-[11px] text-foreground/60 mt-1">
                  Vu : {new Date(selected.last_seen).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" })}
                </div>
              )}
              <div className="text-[10px] font-mono text-foreground/40 mt-0.5 truncate">
                {selected.user_id?.slice(0, 12)}…
              </div>
            </div>
            <button
              type="button"
              onClick={() => setSelected(null)}
              aria-label="Fermer"
              className="h-7 w-7 rounded-full bg-black/5 grid place-items-center hover:bg-black/10"
            >
              <X className="h-3.5 w-3.5" strokeWidth={3} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function FilterChip({ active, onToggle, color, children, testid }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      data-testid={testid}
      className={
        "inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[11px] font-[Outfit] font-black uppercase tracking-widest border-2 border-black transition " +
        (active ? "shadow-[0_2px_0_0_#000]" : "opacity-40 bg-white")
      }
      style={active ? { backgroundColor: color, color: "#fff" } : {}}
    >
      {children}
    </button>
  );
}
